/**
 * Vault de Winclap — contratos compartidos entre backend y frontend.
 *
 * Gestor de contraseñas dentro de Internal para las credenciales que no se
 * pueden reducir a accesos individuales (doc "Seguridad de accesos y
 * credenciales", §6). Cifrado con AWS KMS (Opción A: el servidor puede
 * descifrar — está documentado, no escondido); visibilidad por área del
 * community map; log de auditoría append-only.
 *
 * Todo lo que es constante + label vive acá para que el front no duplique.
 */

// ─── Permisos ─────────────────────────────────────────────────────────────────

/** Tres niveles y nada más (lección de Passbolt). `owner` siempre es una persona. */
export const VAULT_PERMISSIONS = ['read', 'edit', 'owner'] as const;
export type VaultPermission = (typeof VAULT_PERMISSIONS)[number];

export const VAULT_PERMISSION_LEVEL: Record<VaultPermission, number> = { read: 1, edit: 2, owner: 3 };
export const VAULT_PERMISSION_LABEL: Record<VaultPermission, string> = {
  read: 'Leer',
  edit: 'Editar',
  owner: 'Owner',
};

/** Rol de una persona dentro de un grupo ad-hoc. `manager` administra miembros. */
export const VAULT_GROUP_ROLES = ['manager', 'editor', 'reader'] as const;
export type VaultGroupRole = (typeof VAULT_GROUP_ROLES)[number];
export const VAULT_GROUP_ROLE_LABEL: Record<VaultGroupRole, string> = {
  manager: 'Manager',
  editor: 'Editar',
  reader: 'Leer',
};
/** Tope de permiso que un rol de grupo puede heredar de un grant al grupo. */
export const VAULT_GROUP_ROLE_CAP: Record<VaultGroupRole, VaultPermission> = {
  manager: 'edit',
  editor: 'edit',
  reader: 'read',
};

// ─── Principals ───────────────────────────────────────────────────────────────

/**
 * Un principal es a quién se le otorga acceso a un secreto:
 *   user#<email>            — una persona
 *   group#area:<grupo>      — grupo de área (= grupo de Cognito sincronizado del community map)
 *   group#adhoc:<groupId>   — grupo armado a mano (proyecto / cliente)
 */
export const userPrincipal = (email: string) => `user#${email.toLowerCase()}`;
export const areaGroupPrincipal = (cognitoGroup: string) => `group#area:${cognitoGroup}`;
export const adhocGroupPrincipal = (groupId: string) => `group#adhoc:${groupId}`;

export function parsePrincipal(
  principal: string
): { kind: 'user'; email: string } | { kind: 'area'; group: string } | { kind: 'adhoc'; groupId: string } | null {
  if (principal.startsWith('user#')) return { kind: 'user', email: principal.slice(5) };
  if (principal.startsWith('group#area:')) return { kind: 'area', group: principal.slice(11) };
  if (principal.startsWith('group#adhoc:')) return { kind: 'adhoc', groupId: principal.slice(12) };
  return null;
}

// ─── Políticas ────────────────────────────────────────────────────────────────

export const VAULT_POLICY = {
  /** Días sin rotar antes de marcar "requiere rotación". */
  rotationDays: 90,
  /** Vida del token de desbloqueo (passphrase verificada). */
  unlockMinutes: 15,
  /** Segundos que la contraseña queda visible al revelarla. */
  revealSeconds: 30,
  /** Intentos fallidos de passphrase antes del bloqueo. */
  maxUnlockAttempts: 5,
  /** Duración del bloqueo. */
  lockoutMinutes: 15,
  /** Largo mínimo de la passphrase. */
  passphraseMinLength: 12,
  /** Vida máxima de un link temporal. */
  shareLinkMaxHours: 72,
  /**
   * Link de onboarding DORMIDO (13/10/2026): se mintea en el alta y se activa el día de
   * ingreso; si la persona nunca ingresa, el TTL lo borra solo a estos días.
   */
  onboardingDormantLinkDays: 120,
  /** Versiones anteriores de la contraseña que se conservan. */
  historyVersions: 5,
  /** Tamaño máximo de las notas (cifradas). */
  notesMaxChars: 8_000,
} as const;

// ─── Auditoría ────────────────────────────────────────────────────────────────

export const VAULT_AUDIT_ACTIONS = [
  'passphrase_set',
  'passphrase_changed',
  'passphrase_reset',
  'unlock',
  'unlock_failed',
  'locked_out',
  'secret_created',
  'secret_updated',
  'secret_revealed',
  'totp_read',
  'secret_rotated',
  'secret_deleted',
  'grant_added',
  'grant_revoked',
  'owner_reassigned',
  'group_created',
  'group_member_added',
  'group_member_removed',
  'group_member_role_changed',
  'group_deleted',
  'link_created',
  'link_activated',
  'link_viewed',
  'link_revoked',
  'rotation_flagged',
  'access_denied',
  'census_owner_assigned',
  'census_form_sent',
  'census_form_submitted',
  'census_updated',
] as const;
export type VaultAuditAction = (typeof VAULT_AUDIT_ACTIONS)[number];

export const VAULT_AUDIT_ACTION_LABEL: Record<VaultAuditAction, string> = {
  passphrase_set: 'Creó su passphrase',
  passphrase_changed: 'Cambió su passphrase',
  passphrase_reset: 'Passphrase reseteada por IT',
  unlock: 'Desbloqueó el Vault',
  unlock_failed: 'Passphrase incorrecta',
  locked_out: 'Bloqueado por intentos',
  secret_created: 'Creó credencial',
  secret_updated: 'Editó credencial',
  secret_revealed: 'Reveló secreto',
  totp_read: 'Copió TOTP',
  secret_rotated: 'Rotó contraseña',
  secret_deleted: 'Eliminó credencial',
  grant_added: 'Otorgó acceso',
  grant_revoked: 'Revocó acceso',
  owner_reassigned: 'Reasignó owner',
  group_created: 'Creó grupo',
  group_member_added: 'Agregó a grupo',
  group_member_removed: 'Sacó de grupo',
  group_member_role_changed: 'Cambió rol en grupo',
  group_deleted: 'Eliminó grupo',
  link_created: 'Creó link temporal',
  link_activated: 'Activó link temporal',
  link_viewed: 'Link temporal abierto',
  link_revoked: 'Revocó link temporal',
  rotation_flagged: 'Marcó para rotación',
  access_denied: 'Acceso denegado',
  census_owner_assigned: 'Asignó dueño (censo)',
  census_form_sent: 'Envió formulario (censo)',
  census_form_submitted: 'Respondió formulario (censo)',
  census_updated: 'Actualizó cuenta (censo)',
};

/** Los que cuentan como "alerta" en el tile de auditoría. */
export const VAULT_ALERT_ACTIONS: readonly VaultAuditAction[] = ['access_denied', 'locked_out', 'unlock_failed'];

export interface VaultAuditEvent {
  auditId: string;
  action: VaultAuditAction;
  actorEmail: string;
  ts: string;      // ISO
  day: string;     // YYYY-MM-DD (GSI)
  secretId?: string;
  secretName?: string;
  targetEmail?: string;
  groupId?: string;
  linkId?: string;
  ip?: string;
  userAgent?: string;
  detail?: string;
}

// ─── Entidades ────────────────────────────────────────────────────────────────

/** Estado que el front necesita para decidir qué pantalla mostrar. */
export interface VaultMyAccess {
  /** Desde el 28/09/2026 es true para todo usuario autenticado (false solo si no se pudo resolver el acceso). */
  canView: boolean;
  isAdmin: boolean;
  /** null = no se pudo consultar la Directory API (el Vault cierra cerrado) */
  mfaEnrolled: boolean | null;
  hasPassphrase: boolean;
  lockedUntil: string | null;
  principals: string[];
}

export interface VaultSecretMeta {
  secretId: string;
  name: string;
  username: string;
  url?: string;
  area?: string;
  clientId?: string;
  tags?: string[];
  hasTotp: boolean;
  ownerEmail: string;
  needsRotation: boolean;
  lastRotatedAt: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  version: number;
  /** El permiso efectivo de quien pregunta (lo agrega el router). null = admin sin grant (ve metadata, no revela). */
  myPermission?: VaultPermission | null;
}

/** Áreas del community map que son grupo de Cognito (espejo de AREA_GROUP_RULES en cognito-area-sync.ts). */
export const VAULT_AREAS = [
  { id: 'paidmedia', label: 'Paid Media' },
  { id: 'ownedmedia', label: 'Owned Media' },
  { id: 'creative', label: 'Creative' },
  { id: 'data', label: 'Data' },
  { id: 'tech', label: 'Tech' },
  { id: 'finance', label: 'Finance' },
  { id: 'people', label: 'People' },
  { id: 'business', label: 'Business' },
  { id: 'partnerships', label: 'Partnerships' },
  { id: 'growth', label: 'Growth' },
  { id: 'branding', label: 'Branding' },
  { id: 'executive', label: 'Executive' },
] as const;
export type VaultAreaId = (typeof VAULT_AREAS)[number]['id'];
export const VAULT_AREA_LABEL: Record<string, string> = Object.fromEntries(VAULT_AREAS.map((a) => [a.id, a.label]));

/** Lo que devuelve `vault.getSecret`: metadata + accesos + actividad (nunca el secreto). */
export interface VaultSecretDetail {
  meta: Omit<VaultSecretMeta, 'myPermission'> & { myPermission: VaultPermission | null };
  grants: VaultGrant[];
  hasNotes: boolean;
  totp: { digits: number; step: number } | null;
  recentActivity: VaultAuditEvent[];
  /** Días hasta que la política de rotación la marque (negativo = vencida). */
  rotationDueInDays: number;
}

export interface VaultHealthStats {
  total: number;
  needsRotation: number;
  withoutTotp: number;
  owned: number;
  /** Cuentas compartidas del censo; null en la vista de un usuario sin acceso al censo. */
  sharedAccounts: number | null;
}

export interface VaultGrant {
  secretId: string;
  principal: string;
  permission: VaultPermission;
  grantedBy: string;
  grantedAt: string;
}

export interface VaultGroup {
  groupId: string;
  name: string;
  description?: string;
  responsibleEmail: string;
  createdBy: string;
  createdAt: string;
  memberCount?: number;
}

export interface VaultGroupMember {
  groupId: string;
  email: string;
  role: VaultGroupRole;
  addedBy: string;
  addedAt: string;
}

// ─── Censo de cuentas compartidas ─────────────────────────────────────────────

/** Árbol de decisión A/B/C/D del doc (§5.3) + los dos estados terminales que faltaban. */
export const CENSUS_DESTINOS = ['grupo', 'individual', 'service', 'vault', 'baja', 'relevar'] as const;
export type CensusDestino = (typeof CENSUS_DESTINOS)[number];
export const CENSUS_DESTINO_LABEL: Record<CensusDestino, string> = {
  grupo: 'Grupo / alias de Workspace',
  individual: 'Accesos individuales',
  service: 'Service account / API',
  vault: 'Vault (irreducible)',
  baja: 'Dar de baja',
  relevar: 'A relevar',
};

export const CENSUS_ESTADOS = ['sin_relevar', 'en_analisis', 'relevada'] as const;
export type CensusEstado = (typeof CENSUS_ESTADOS)[number];
export const CENSUS_ESTADO_LABEL: Record<CensusEstado, string> = {
  sin_relevar: 'Sin relevar',
  en_analisis: 'En análisis',
  relevada: 'Relevada',
};

/** Pasos del asistente de MFA (máquina de estados del front; el backend valida el enum). */
export const MFA_ASSISTANT_STEPS = ['where', 'google', 'other', 'none', 'pick', 'verify', 'done'] as const;
export type MfaAssistantStep = (typeof MFA_ASSISTANT_STEPS)[number];

/** Lo que devuelve la Function URL pública al abrir un link temporal (POST). */
export interface ShareLinkRevealResponse {
  name: string;
  username: string;
  password: string;
  url: string | null;
  totp: { code: string; expiresIn: number; digits: number } | null;
  createdBy: string;
  expiresAt: string;
  oneTime: boolean;
}

/** Las 4 preguntas clave que responde el dueño propuesto (comentario de Sol en el doc). */
export const CENSUS_QUESTIONS = [
  { key: 'logins', text: '¿Alguien inicia sesión con esta cuenta en alguna plataforma o herramienta? ¿En cuáles?' },
  { key: 'integrations', text: '¿Hay integraciones, scripts o conectores que usen esta cuenta o un token generado desde ella?' },
  { key: 'ownership', text: '¿Esta cuenta es dueña de algo: Business Manager, MCC, licencias, suscripciones, método de pago, o accesos que un cliente le dio directamente?' },
  { key: 'breakage', text: 'Si mañana solo pudiera recibir correo pero no iniciar sesión en ningún lado, ¿qué se rompería?' },
] as const;
export type CensusQuestionKey = (typeof CENSUS_QUESTIONS)[number]['key'];

export interface VaultSharedAccount {
  accountEmail: string;
  probableUse?: string;
  ownerEmail?: string;
  ownerName?: string;
  area?: string;
  declaredUse?: string;
  destino: CensusDestino;
  estado: CensusEstado;
  hasMfa?: boolean | null;
  mfaLocation?: string;
  platforms?: string[];
  answers?: Partial<Record<CensusQuestionKey, string>>;
  notes?: string;
  linkedSecretIds?: string[];
  /** "No es mía, es de…" del dueño propuesto (campo propio: no se concatena a notes). */
  ownerNote?: string;
  /** Solo cuando el DM de Slack llegó; si falló queda formError y no formSentAt. */
  formSentAt?: string;
  formError?: string;
  answeredAt?: string;
  updatedAt: string;
  updatedBy: string;
}
